import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Container, Row, Col, Image } from 'react-bootstrap';
import { Link, useParams } from 'react-router-dom';
import LoadingSpinner from '../components/LoadingSpinner';
import './ProductPage.css';
import { fetchProductById, clearCurrentProduct } from '../store/actions';
import useTitle from '../hooks/useTitle';
import defaultProductImage from '../images/default-product.png';

const ProductPage = () => {
    const dispatch = useDispatch();
    const [selectedImage, setSelectedImage] = useState(0);
    const { categoryName, productNameWithId } = useParams();
    const { data: product, loading, error } = useSelector(state => state.currentProduct);

    // Задаем название сайта
    useTitle(product ? `${product.title}` : '');
        
    // const [showSpinner, setShowSpinner] = useState(false);
    
    // Эффект для отложенного показа спиннера
    // useEffect(() => {
    //     let spinnerTimer;
        
    //     if (loading) {
    //         // Показываем спиннер только если загрузка длится дольше 300мс
    //         spinnerTimer = setTimeout(() => {
    //             setShowSpinner(true);
    //         }, 300);
    //     } else {
    //         setShowSpinner(false);
    //     }
        
    //     return () => {
    //         if (spinnerTimer) clearTimeout(spinnerTimer);
    //     };
    // }, [loading]);

    useEffect(() => {
        // Очищаем старый продукт перед загрузкой нового
        dispatch(clearCurrentProduct());

        if (categoryName && productNameWithId) {
            // Извлекаем ID продукта из URL формата "productName-productId"
            const productId = productNameWithId.split('-').pop();
            if (productId && !isNaN(productId)) {
                dispatch(fetchProductById(productId));
            }
        } 
    }, [categoryName, productNameWithId, dispatch]);

    useEffect(() => {
        if (product && product.images && product.images.length > 0) {
            const mainImageIndex = product.images.findIndex(img => img.isMain === true);
            if (mainImageIndex !== -1) {
                setSelectedImage(mainImageIndex);
            }
        }
    }, [product]);
    
    if (loading) { //   && showSpinner
        return <LoadingSpinner message="Загрузка информации о товаре..." containerHeight="vh-100"/>;
    }

    if (!product && !loading) {
        return (
            <Container className="py-5">
                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '80vh' }}>
                    <h3>Товар не найден</h3>
                </div>
            </Container>
        );
    }

    // Если данные еще загружаются, но спиннер не нужно показывать,
    // возвращаем "невидимую" заглушку того же размера
    if (loading) {
        return (
            <Container className="product-page py-4">
                <div style={{ minHeight: 'calc(100vh - 200px)' }}>
                    {/* Заглушка с теми же размерами */}
                </div>
            </Container>
        );
    }
        
    return (
        <Container className="product-page py-4">
            {/* Хлебные крошки */}
            <div className="breadcrumbs mb-3">
                <Link to="/">Главная</Link> {' > '} 
                <Link to="/catalog">Каталог</Link> {' > '} 
                <Link to={`/catalog/${product.category}`}>{product.categoryTitle}</Link> {' > '} 
                <span>{product.title}</span>
            </div>
            
            <Row>
                {/* Галерея изображений */}
                <Col lg={6} md={6} className="mb-3">
                    <div className="product-gallery">
                        <div className="main-image-container mb-3">
                            <Image 
                                src={product.images?.[selectedImage]?.imagePath || defaultProductImage} 
                                alt={product.title} 
                                className="main-product-image"
                                fluid
                                draggable="false"
                                onDragStart={(e) => e.preventDefault()}
                                onError={(e) => {
                                    e.target.onerror = null; // Предотвращает зацикливание
                                    e.target.src = defaultProductImage;
                                }}
                            />
                        </div>
                        
                        {product.images && product.images.length > 0 && product.images[0].imagePath.length > 0 && (
                            <div className="thumbnail-container">
                                <Row>
                                    {product.images.map((image, index) => (
                                        <Col xs={3} key={index} className="mb-2">
                                            <Image 
                                                src={image.imagePath} 
                                                alt={`${product.title} ${index + 1}`} 
                                                className={`thumbnail-image ${selectedImage === index ? 'active' : ''}`}
                                                draggable="false"
                                                onDragStart={(e) => e.preventDefault()}
                                                onClick={() => setSelectedImage(index)}
                                                onError={(e) => {
                                                    e.target.onerror = null; // Предотвращает зацикливание
                                                    e.target.src = defaultProductImage;
                                                }}
                                            />
                                        </Col>
                                    ))}
                                </Row>
                            </div>
                        )}
                    </div>
                </Col>
                
                {/* Информация о товаре */}
                <Col lg={6} md={6}>
                    <h1 className="product-title">{product.title}</h1>
                    {/* <p className="product-article">Артикул: {product.article}</p> */}
                    {/* Отображаем артикул рядом с названием, только если он один */}
                    {product.articles.length === 1 && (
                        <p className="product-article">Артикул: {product.articles[0].code}</p>
                    )}
                    <div className="product-price mb-4">
                        {product.price !== null ? `${product.price} ₽` : "Цена по запросу"}
                    </div>
                    
                    {/* Характеристики товара */}
                    {product.characteristics && product.characteristics.length > 0 ? (
                        <div className="product-characteristics mb-4">
                            <h3 className="characteristics-title">Характеристики</h3>
                            <table className="characteristics-table">
                                <tbody>
                                    {product.characteristics.map((char, index) => (
                                        <tr key={index}>
                                            <td className="char-name">{char.name}</td>
                                            <td className="char-value">{char.value}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        product.description && product.description.trim() !== '' && (
                            <div className="product-description mb-4">
                                <h3 className="description-title">Описание</h3>
                                <p>{product.description}</p>
                            </div>
                        )
                    )}
                </Col>
            </Row>
            
            {/* Описание товара */}
            {product.characteristics && product.characteristics.length > 0 && product.description && product.description.trim() !== '' && (
                <Row className="mt-4">
                    <Col xs={12}>
                        <div className="product-description">
                            <h3 className="description-title">Описание</h3>
                            <p>{product.description}</p>
                        </div>
                    </Col>
                </Row>
            )}

            {/* Таблица артикулов (отображается только если есть несколько артикулов) */}
            {product.articles.length > 1 && (
                <Row className="mt-1">
                    <Col xs={12}>
                        <div className="product-articles">
                            <div className="articles-header-container">
                                <h3 className="articles-title">Варианты товара</h3>
                            </div>
                            <div className="articles-table-container">
                                <table className="articles-table">
                                    <thead>
                                        <tr>
                                            <th>Артикул</th>
                                            <th>Наименование</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {product.articles.map((article, index) => (
                                            <tr key={index}>
                                                <td>{article.code}</td>
                                                <td>{article.name}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </Col>
                </Row>
            )}
        </Container>
    );
};

export default ProductPage;